import { filterLocaisCentro } from '@acme/common'

export type FacetaId = 'empresas' | 'centrosCusto' | 'locais' | 'funcionarios'
export type ValorFaceta = number | number[] | null | undefined
export type Escopo = Partial<Record<FacetaId, ValorFaceta>> & {
    incluirDemitidos?: boolean
}
export type Registro = {
    id: FacetaId
    multiple: boolean
    /** lista as opções de todas as empresas do cliente, ignorando o escopo */
    listAll?: boolean
}

/** Ordem canônica das facetas — empresa → [centro de custo ∥ locais] → funcionário. */
export const FACETAS: FacetaId[] = [
    'empresas',
    'centrosCusto',
    'locais',
    'funcionarios',
]

export const LABEL: Record<FacetaId, string> = {
    empresas: 'Empresa',
    centrosCusto: 'Centros de custo',
    locais: 'Locais',
    funcionarios: 'Funcionários',
}

/** singular/plural por extenso — o termo que os badges revelam no hover */
const CONTAGEM: Record<FacetaId, [singular: string, plural: string]> = {
    empresas: ['empresa', 'empresas'],
    centrosCusto: ['centro de custo', 'centros de custo'],
    locais: ['local', 'locais'],
    funcionarios: ['funcionário', 'funcionários'],
}

export const arr = (v: ValorFaceta): number[] =>
    v == null
        ? []
        : Array.isArray(v)
          ? v.filter(id => id > 0)
          : v > 0
            ? [v]
            : []

export const um = (v: ValorFaceta): number | null => arr(v)[0] ?? null

export const varios = (v: ValorFaceta): number[] => arr(v)

/** Valor "vazio" de uma faceta — é o registro que diz se é `[]` ou `null`. */
export function vazioDe(registro: Registro[], id: FacetaId): number[] | null {
    return registro.find(r => r.id === id)?.multiple ? [] : null
}

/**
 * Aplica um valor a uma faceta e propaga a cascata de reset: empresa zera
 * centros de custo, locais e funcionários; centro de custo e local zeram
 * funcionários. Só toca facetas registradas.
 */
export function aplicarFaceta(
    escopo: Escopo,
    registro: Registro[],
    id: FacetaId,
    valor: ValorFaceta,
): Escopo {
    const registrada = (faceta: FacetaId) => registro.some(r => r.id === faceta)
    const proximo: Escopo = { ...escopo, [id]: valor }

    if (id !== 'funcionarios' && registrada('funcionarios'))
        proximo.funcionarios = vazioDe(registro, 'funcionarios')

    if (id === 'empresas') {
        if (registrada('centrosCusto'))
            proximo.centrosCusto = vazioDe(registro, 'centrosCusto')
        if (registrada('locais')) proximo.locais = vazioDe(registro, 'locais')
    }

    return proximo
}

/**
 * Volta ao estado inicial. Alcança as quatro facetas, não só as registradas:
 * uma parte escondida por agrupamento (locais em Absenteísmo/Faltas e atrasos)
 * saiu do registro mas o valor dela continua no escopo filtrando linhas.
 */
export function limparEscopo(registro: Registro[]): Escopo {
    const vazio: Escopo = {}
    for (const id of FACETAS) vazio[id] = vazioDe(registro, id)
    return vazio
}

/**
 * Faceta que o rail deve destacar. Mantém a atual enquanto ela existir — uma
 * parte que desmonta (faceta condicional) sai do registro e deixaria o painel
 * em branco.
 */
export function proximaAtiva(
    registro: Registro[],
    ativa: FacetaId | null,
): FacetaId | null {
    if (ativa && registro.some(r => r.id === ativa)) return ativa
    return registro[0]?.id ?? null
}

/** só o termo da faceta, pluralizado pelo total (`locais`, `local`). */
export function palavraContagem(id: FacetaId, total: number): string {
    const [singular, plural] = CONTAGEM[id]
    return total === 1 ? singular : plural
}

export type BadgeFaceta = { id: FacetaId; total: number }

export type ResumoVisual = {
    /** nome da 1ª empresa escolhida; null se nenhuma empresa foi escolhida ou os nomes ainda não chegaram */
    empresaTexto: string | null
    /** nome do funcionário quando a faceta é single e há um escolhido — em tela de um funcionário só, é ele que importa no botão */
    funcionarioTexto: string | null
    /** número do badge de empresas: as que o texto não representa, ou o total escolhido quando não há texto; 0 = sem badge de empresa */
    empresasExtras: number
    /** `+` quando o número é uma sobra do texto; vazio quando ele já é o total */
    empresasPrefixo: '+' | ''
    /** facetas não-empresa com seleção explícita */
    badges: BadgeFaceta[]
}

/**
 * Resumo visual do escopo: nome da empresa como texto simples + um badge por
 * faceta (não-empresa) com seleção explícita. Funcionários só vira badge com
 * seleção: badge é filtro ativo, não alcance do filtro — o alcance fica no
 * painel.
 */
export function montarResumoVisual({
    escopo,
    registro,
    nomesEmpresas,
    nomeFuncionario,
}: {
    escopo: Escopo
    registro: Registro[]
    nomesEmpresas?: string[]
    /** nome do funcionário escolhido, quando a faceta é de seleção única */
    nomeFuncionario?: string
}): ResumoVisual {
    const facetaFuncionarioSingle = registro.some(
        parte => parte.id === 'funcionarios' && !parte.multiple,
    )
    const funcionarioTexto =
        facetaFuncionarioSingle && arr(escopo.funcionarios).length === 1
            ? (nomeFuncionario ?? null)
            : null

    const escolhidas = arr(escopo.empresas).length
    // com o nome do funcionário no texto, a empresa desce para badge
    const empresaTexto = funcionarioTexto ? null : (nomesEmpresas?.[0] ?? null)
    const empresasExtras = escolhidas - (empresaTexto ? 1 : 0)
    const empresasPrefixo = empresaTexto ? '+' : ''

    const badges = registro
        .filter(parte => parte.id !== 'empresas')
        .filter(parte => !(funcionarioTexto && parte.id === 'funcionarios'))
        .map(({ id }) => ({ id, total: arr(escopo[id]).length }))
        .filter(({ total }) => total > 0)

    return {
        empresaTexto,
        funcionarioTexto,
        empresasExtras,
        empresasPrefixo,
        badges,
    }
}

/** Params que a listagem de funcionário recebe quando o funil for server-side. */
export function queryParamsDoEscopo(escopo: Escopo) {
    const params: {
        empresaIds?: number[]
        centroCustoIds?: number[]
        locaisIds?: number[]
    } = {}

    const empresas = arr(escopo.empresas)
    const centrosCusto = arr(escopo.centrosCusto)
    const locais = arr(escopo.locais)

    if (empresas.length) params.empresaIds = empresas
    if (centrosCusto.length) params.centroCustoIds = centrosCusto
    if (locais.length) params.locaisIds = locais

    return params
}

/** Funil client-side: funcionário precisa bater em centro de custo ∩ local. */
export function filtrarNoEscopo<T>(
    itens: T[],
    escopo: Escopo,
    accessor: (item: T) => any = item => item,
): T[] {
    return itens.filter(
        filterLocaisCentro<T>(
            arr(escopo.locais),
            arr(escopo.centrosCusto),
            accessor,
        ),
    )
}

/** O store usa -1 como "não filtrar"; o escopo usa null/[]. */
export function storeParaEscopo({
    companyId,
    costCenterIds,
    locaisIds,
    employeeIds,
    incluirDemitidos,
}: {
    companyId: number
    costCenterIds: number[]
    locaisIds: number[]
    employeeIds?: number[]
    incluirDemitidos?: boolean
}): Escopo {
    return {
        empresas: companyId > 0 ? companyId : null,
        centrosCusto: arr(costCenterIds),
        locais: arr(locaisIds),
        funcionarios: arr(employeeIds),
        incluirDemitidos: !!incluirDemitidos,
    }
}

/** propagarFuncionarios: false` não define employeeIds */
export function escopoParaStore(
    escopo: Escopo,
    { propagarFuncionarios = true }: { propagarFuncionarios?: boolean } = {},
) {
    const centrosCusto = arr(escopo.centrosCusto)
    const funcionarios = arr(escopo.funcionarios)

    return {
        companyId: um(escopo.empresas) ?? -1,
        costCenterIds: centrosCusto,
        costCenterId: centrosCusto[0] ?? -1,
        locaisIds: arr(escopo.locais),
        employeeIds: propagarFuncionarios ? funcionarios : [],
        employeeId: funcionarios[0] ?? -1,
        incluirDemitidos: !!escopo.incluirDemitidos,
    }
}

/**
 * Guias da árvore de uma lista achatada de níveis (`indent`).
 *
 * Devolve, para cada item, um booleano por nível acima dele: `true` quando a
 * linha daquele nível continua abaixo — há outro item do mesmo nível antes de
 * algum item mais raso encerrar o bloco. O último booleano é o do próprio
 * nível do item: `false` ali significa que ele é o último irmão.
 */
export function guiasDaArvore(niveis: number[]): boolean[][] {
    const guias: boolean[][] = []
    /** por nível, se já apareceu um irmão abaixo da linha sendo montada */
    const continua: boolean[] = []

    for (let i = niveis.length - 1; i >= 0; i--) {
        const nivel = niveis[i]

        guias[i] = Array.from({ length: nivel }, (_, j) => !!continua[j + 1])

        // este item é mais raso que tudo que veio depois: encerra esses blocos
        continua.length = nivel + 1
        continua[nivel] = true
    }

    return guias
}
